import { MarkdownRenderer, useDebouncedValue } from "@adafia/ui";
import "./Editor.css";
import {
	Check,
	CheckCircle2,
	Cloud,
	CloudOff,
	Columns2,
	Eye,
	FilePenLine,
	Globe,
	Lock,
	RefreshCw,
	Save,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { uploadImage } from "@/notes/api/notes";
import type { FileNode } from "@/notes/types";

type ViewMode = "editor" | "preview" | "split";
interface EditorProps {
	activeNote: FileNode;
	isSaving: boolean;
	isDirty: boolean;
	autoSave: boolean;
	onToggleAutoSave: () => void;
	onChange: (value: string) => void;
	onSave: () => void;
	onTogglePublish: () => void;
}

const ViewToggles = ({
	viewMode,
	setViewMode,
}: {
	viewMode: ViewMode;
	setViewMode: (mode: ViewMode) => void;
}) => (
	<div className="view-toggle-container">
		<button
			className={viewMode === "editor" ? "active" : ""}
			title="Editor view"
			type="button"
			onClick={() => setViewMode("editor")}
		>
			<FilePenLine size={18} />
			<span className="view-label">Edit</span>
		</button>
		<button
			className={viewMode === "split" ? "active" : ""}
			title="Split view"
			type="button"
			onClick={() => setViewMode("split")}
		>
			<Columns2 size={18} />
			<span className="view-label">Split</span>
		</button>
		<button
			className={viewMode === "preview" ? "active" : ""}
			title="Preview view"
			type="button"
			onClick={() => setViewMode("preview")}
		>
			<Eye size={18} />
			<span className="view-label">Preview</span>
		</button>
	</div>
);

const StatusIndicator = ({
	isSaving,
	isDirty,
}: {
	isSaving: boolean;
	isDirty: boolean;
}) => (
	<div className="status-indicator">
		{isSaving ? (
			<span className="saving-status" title="Saving changes...">
				<RefreshCw className="spin-icon" size={14} />
				<span className="status-text">Saving...</span>
			</span>
		) : isDirty ? (
			<span className="dirty-status" title="Unsaved changes">
				<div className="dirty-dot" />
				<span className="status-text">Unsaved</span>
			</span>
		) : (
			<span className="saved-status" title="All changes saved">
				<Check size={14} />
				<span className="status-text">Saved</span>
			</span>
		)}
	</div>
);

const ActionGroup = ({
	autoSave,
	isPublished,
	isSaving,
	isDirty,
	onToggleAutoSave,
	onTogglePublish,
	onSave,
}: {
	autoSave: boolean;
	isPublished: boolean;
	isSaving: boolean;
	isDirty: boolean;
	onToggleAutoSave: () => void;
	onTogglePublish: () => void;
	onSave: () => void;
}) => (
	<div className="action-group">
		<button
			className={`auto-save-toggle ${autoSave ? "active" : ""}`}
			onClick={onToggleAutoSave}
			title={autoSave ? "Disable auto-save" : "Enable auto-save"}
			type="button"
		>
			{autoSave ? <Cloud size={18} /> : <CloudOff size={18} />}
		</button>

		<button
			className={`publish-button ${isPublished ? "published" : ""}`}
			title={isPublished ? "Unpublish" : "Publish"}
			type="button"
			onClick={onTogglePublish}
		>
			{isPublished ? <Globe size={18} /> : <Lock size={18} />}
			<span className="publish-label">
				{isPublished ? "Public" : "Private"}
			</span>
		</button>

		<button
			className={`save-button ${isDirty && !autoSave ? "highlight" : ""}`}
			title="Save (Ctrl+S)"
			type="button"
			onClick={onSave}
			disabled={isSaving || !isDirty}
		>
			<Save size={18} />
			<span className="save-label">Save</span>
		</button>
	</div>
);

const EditorHeader = ({
	activeNote,
	isSaving,
	isDirty,
	autoSave,
	viewMode,
	onToggleAutoSave,
	onTogglePublish,
	onSave,
	setViewMode,
}: {
	activeNote: FileNode;
	isSaving: boolean;
	isDirty: boolean;
	autoSave: boolean;
	viewMode: ViewMode;
	onToggleAutoSave: () => void;
	onTogglePublish: () => void;
	onSave: () => void;
	setViewMode: (mode: ViewMode) => void;
}) => {
	const charCount = useMemo(
		() => (activeNote.content || "").length,
		[activeNote.content],
	);

	return (
		<header className="editor-header">
			<div className="editor-header-left">
				<div className="title-container">
					<span className="editor-title">{activeNote.title}</span>
					<div className="editor-meta">
						<span className="char-count">{charCount} characters</span>
						{activeNote.isPublished && (
							<span className="published-badge">
								<CheckCircle2 size={12} /> Published
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="editor-header-center">
				<ViewToggles viewMode={viewMode} setViewMode={setViewMode} />
			</div>

			<div className="editor-header-right">
				<StatusIndicator isSaving={isSaving} isDirty={isDirty} />
				<ActionGroup
					autoSave={autoSave}
					isPublished={activeNote.isPublished ?? false}
					isSaving={isSaving}
					isDirty={isDirty}
					onToggleAutoSave={onToggleAutoSave}
					onTogglePublish={onTogglePublish}
					onSave={onSave}
				/>
			</div>
		</header>
	);
};

export const Editor: React.FC<EditorProps> = ({
	activeNote,
	isSaving,
	isDirty,
	autoSave,
	onToggleAutoSave,
	onChange,
	onSave,
	onTogglePublish,
}) => {
	const [viewMode, setViewMode] = useState<ViewMode>(() =>
		window.innerWidth < 768 ? "editor" : "split",
	);
	const [isDragging, setIsDragging] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const content = activeNote.content || "";

	// Debounce the preview content to avoid heavy re-renders on every keystroke
	const debouncedContent = useDebouncedValue(content, 300);

	// Global Ctrl+S or Cmd+S shortcut for saving
	useEffect(() => {
		const handleGlobalKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
				e.preventDefault();
				onSave();
			}
		};
		window.addEventListener("keydown", handleGlobalKeyDown);
		return () => {
			window.removeEventListener("keydown", handleGlobalKeyDown);
		};
	}, [onSave]);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Support Ctrl+S or Cmd+S for saving
		if ((e.ctrlKey || e.metaKey) && e.key === "s") {
			e.preventDefault();
			onSave();
			return;
		}

		// Support Tab for indentation
		if (e.key === "Tab") {
			e.preventDefault();
			const textarea = textareaRef.current;
			if (!textarea) return;

			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const value = textarea.value;

			// Insert tab character at current position
			const newValue = `${value.substring(0, start)}\t${value.substring(end)}`;
			onChange(newValue);

			// Reset cursor position (need to wait for React to re-render)
			setTimeout(() => {
				textarea.selectionStart = textarea.selectionEnd = start + 1;
			}, 0);
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		if (e.dataTransfer.types.includes("Files")) {
			setIsDragging(true);
		}
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		setIsDragging(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length === 0) return;

		await insertImageFiles(files);
	};

	const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		const files = Array.from(e.clipboardData.files);
		if (files.length > 0) {
			e.preventDefault();
			await insertImageFiles(files);
			return;
		}

		// Check if pasted content is a direct image URL
		const pastedText = e.clipboardData.getData("text/plain");
		if (pastedText) {
			const isImageUrl =
				/^(https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp|svg|ico))(?:\?.*)?$/i.test(
					pastedText.trim(),
				);
			if (isImageUrl) {
				e.preventDefault();
				const textarea = textareaRef.current;
				if (!textarea) return;

				const start = textarea.selectionStart;
				const end = textarea.selectionEnd;
				const value = textarea.value;

				const markdownImage = `![Image](${pastedText.trim()})`;
				const newValue =
					value.substring(0, start) + markdownImage + value.substring(end);
				onChange(newValue);

				setTimeout(() => {
					textarea.focus();
					textarea.selectionStart = textarea.selectionEnd =
						start + markdownImage.length;
				}, 0);
			}
		}
	};

	const insertImageFiles = async (files: File[]) => {
		const imageFiles = files.filter((file) => file.type.startsWith("image/"));
		if (imageFiles.length === 0) return;

		const textarea = textareaRef.current;
		if (!textarea) return;

		const start = textarea.selectionStart;
		const end = textarea.selectionEnd;
		const value = textarea.value;

		// Create placeholders for each file
		const placeholders = imageFiles.map(
			(file) => `\n![Uploading ${file.name}...]()\n`,
		);
		const insertionText = placeholders.join("");

		// Insert placeholders immediately at the cursor position
		const newValueWithPlaceholders =
			value.substring(0, start) + insertionText + value.substring(end);
		onChange(newValueWithPlaceholders);

		// Focus the textarea and set selection to after the inserted placeholders
		setTimeout(() => {
			textarea.focus();
			textarea.selectionStart = textarea.selectionEnd =
				start + insertionText.length;
		}, 0);

		// Upload files in parallel and replace placeholders
		for (let i = 0; i < imageFiles.length; i++) {
			const file = imageFiles[i];
			const placeholder = placeholders[i];
			if (!file || !placeholder) continue;

			(async () => {
				try {
					const uploadResult = await uploadImage(file);
					const finalMarkdown = `\n![${file.name}](${uploadResult.url})\n`;

					// Re-read current textarea value because other edits or uploads might have occurred
					const currentText =
						textareaRef.current?.value || newValueWithPlaceholders;
					const updatedText = currentText.replace(placeholder, finalMarkdown);
					onChange(updatedText);
				} catch (err) {
					console.error(`Failed to upload ${file.name}:`, err);
					const errorMarkdown = `\n![Upload failed: ${file.name}]()\n`;
					const currentText =
						textareaRef.current?.value || newValueWithPlaceholders;
					const updatedText = currentText.replace(placeholder, errorMarkdown);
					onChange(updatedText);
				}
			})();
		}
	};

	const editorPane = (
		<div
			className={`editor-pane ${viewMode === "split" ? "split-view" : ""} ${isDragging ? "drag-over" : ""}`}
		>
			<textarea
				ref={textareaRef}
				className="editor-textarea"
				value={content}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onPaste={handlePaste}
				placeholder="Write your thoughts in Markdown..."
				spellCheck={false}
			/>
		</div>
	);

	const previewPane = (
		<div className="preview-pane">
			<MarkdownRenderer content={debouncedContent} />
		</div>
	);

	return (
		<div className="editor-wrapper">
			<EditorHeader
				activeNote={activeNote}
				isSaving={isSaving}
				isDirty={isDirty}
				autoSave={autoSave}
				viewMode={viewMode}
				onToggleAutoSave={onToggleAutoSave}
				onTogglePublish={onTogglePublish}
				onSave={onSave}
				setViewMode={setViewMode}
			/>
			<div className="editor-content">
				{viewMode === "editor" && editorPane}
				{viewMode === "preview" && previewPane}
				{viewMode === "split" && (
					<>
						{editorPane}
						{previewPane}
					</>
				)}
			</div>
		</div>
	);
};
